/**
 * Electric-instrument status / data ramps shared across brands.
 *
 * Slightly higher chroma than stock Aura so they sit next to Indutrust sky
 * `#1fa8f5` without reading as acid neon. Applied once via ThemeStore
 * `updatePreset({ primitive })` and brand `definePreset` so `--p-green-*`,
 * `--p-orange-*`, `--p-red-*`, `--p-purple-*`, and `--p-yellow-*` resolve
 * everywhere (tags, messages, charts, focal marks).
 *
 * Roles (see DESIGN.md):
 * - **green** — success / verified / positive trend
 * - **orange** — warn / gaps / incomplete
 * - **red** — danger / error / negative trend
 * - **purple** — help / categorical data / one focal mark
 * - **yellow** — caution / hotspot / incomplete dots
 */

export const ELECTRIC_GREEN_PALETTE: Record<string, string> = {
  50: '#ecfdf5',
  100: '#d1fadf',
  200: '#a6f4c5',
  300: '#6ce9a6',
  400: '#32d583',
  500: '#12b76a',
  600: '#039855',
  700: '#027a48',
  800: '#05603a',
  900: '#054f31',
  950: '#022c1b',
};

export const ELECTRIC_ORANGE_PALETTE: Record<string, string> = {
  50: '#fff6ed',
  100: '#ffead5',
  200: '#fddcab',
  300: '#feb273',
  400: '#fd853a',
  500: '#fb6514',
  600: '#ec4a0a',
  700: '#c4320a',
  800: '#9c2a10',
  900: '#7e2410',
  950: '#511c10',
};

export const ELECTRIC_RED_PALETTE: Record<string, string> = {
  50: '#fef3f2',
  100: '#fee4e2',
  200: '#fecdc9',
  300: '#fda29b',
  400: '#f97066',
  500: '#f04438',
  600: '#d92d20',
  700: '#b42318',
  800: '#912018',
  900: '#7a271a',
  950: '#55160c',
};

export const ELECTRIC_PURPLE_PALETTE: Record<string, string> = {
  50: '#f4f3ff',
  100: '#ebe9fe',
  200: '#d9d6fe',
  300: '#bdb4fe',
  400: '#9b8afb',
  500: '#7a5af8',
  600: '#6938ef',
  700: '#5925dc',
  800: '#4a1fb8',
  900: '#3e1c96',
  950: '#27115f',
};

export const ELECTRIC_YELLOW_PALETTE: Record<string, string> = {
  50: '#fffaeb',
  100: '#fef0c7',
  200: '#fedf89',
  300: '#fec84b',
  400: '#fdb022',
  500: '#f79009',
  600: '#dc6803',
  700: '#b54708',
  800: '#93370d',
  900: '#7a2e0e',
  950: '#4e1d09',
};

/**
 * Primitive map passed to `updatePreset` / `definePreset` so Aura's green /
 * orange / red / purple / yellow tokens resolve to the electric family.
 */
export const ELECTRIC_STATUS_PALETTES: Record<
  string,
  Record<string, string>
> = {
  green: ELECTRIC_GREEN_PALETTE,
  orange: ELECTRIC_ORANGE_PALETTE,
  red: ELECTRIC_RED_PALETTE,
  purple: ELECTRIC_PURPLE_PALETTE,
  yellow: ELECTRIC_YELLOW_PALETTE,
};
